import {
  Component,
  Output,
  EventEmitter,
  SimpleChanges,
  OnInit,
  OnChanges,
  HostListener,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import { ViewEncapsulation, ElementRef, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormArray, AbstractControl, FormControl } from '@angular/forms';

import { ReactiveFormsModule } from '@angular/forms';

import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { tap, map, startWith } from 'rxjs/operators';
import { retry, catchError } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';

import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { DeviceDetectorService } from 'ngx-device-detector';

import { DatePipe } from '@angular/common';

import { environment } from '../../environments/environment';
import { Logger } from '@core';
import { AuthQuery } from '@app/auth/auth.query';
import { JournalService, Journal, MailService, Mail } from '@core';
import { CookieService } from 'ngx-cookie-service';
import { AuthDataService } from '@app/auth/auth-data.service';

import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';
import { KundeService, Kunde } from '../services/kunde.service';
import { DataChangeClientService } from '../client/changes-client.service';
import { Subscription } from 'rxjs';


const log = new Logger('ClientEdit');

// Anmerkung interface for JSON array structure
interface Anmerkung {
  user_id: string;
  username: string;
  anmerkung: string;
  datetime: string; // ISO 8601 format
}

@Component({
  selector: 'app-client-edit',
  templateUrl: './client_edit.component.html',
  styleUrls: ['./client_edit.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
})
export class ClientEditComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  
  clientForm: UntypedFormGroup;
  client: Kunde | null = null;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  private subscription: Subscription;
  isNewClient = false;
  environment = environment;

  // Anmerkungen properties
  anmerkungen: Anmerkung[] = [];
  newAnmerkungText: string = '';
  isAddingAnmerkung: boolean = false;
  editingAnmerkungIndex: number | null = null;
  editingAnmerkungText: string = '';
  originalAnmerkungenBeforeEdit: Anmerkung[] = []; // For error recovery
  
  // Track current selected tab index
  selectedTabIndex: number = 0;

  // Getter to check if add button should be disabled
  get canAddAnmerkung(): boolean {
    return !this.isAddingAnmerkung && !!this.newAnmerkungText && this.newAnmerkungText.trim().length > 0;
  }

  /**
   * Handle textarea value change to trigger change detection
   */
  onAnmerkungTextChange(value: string): void {
    this.newAnmerkungText = value;
    // Force change detection to update button state
    this.cdr.detectChanges();
  }

  // Getter for client status options from environment
  get clientStatusOptions() {
    return (environment as any).client_status || [];
  }

  get periodeAufgabenOptions() {
    return (environment as any).periode_aufgaben || [];
  }

  /**
   * Get sorted anmerkungen (oldest first, like a chat)
   */
  get sortedAnmerkungen(): Anmerkung[] {
    if (!this.anmerkungen || this.anmerkungen.length === 0) {
      return [];
    }
    // Sort by datetime ascending (oldest first)
    return [...this.anmerkungen].sort((a, b) => {
      const dateA = new Date(a.datetime).getTime();
      const dateB = new Date(b.datetime).getTime();
      return dateA - dateB;
    });
  }

  constructor(
    private kundeService: KundeService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private authQuery: AuthQuery,
    private dataChangeClientService: DataChangeClientService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private journalService: JournalService,
    private translate: TranslateService
  ) {
    this.createForm();
  }

  ngOnInit() {
    console.log('ClientEditComponent ngOnInit called');
    
    // Load client statuses for dropdown
    
    this.route.params.subscribe(params => {
      const id = params['id'];
      console.log('Route parameter id:', id);
      
      if (id === 'new' || id === '0') {
        console.log('Creating new client');
        this.isNewClient = true;
        this.anmerkungen = []; // Reset anmerkungen array for new client
        this.clientForm.reset({
          suchbegriff: '',
          status_id: 2, // Default to inactive
          techniker_projekthinweis: '',
          startdatum: '',
          enddatum: '',
          anmeldung_besuche: '',
          periode_aufgaben_vorschlagen: 1, // Default to first option
          ueberpruefung_erforderlich: '',
          bemerkung_next_evaluation: '',
          wiederkehrende_aufgaben_vorhanden: 0,
          datum_letzte_wiederkehrende_aufgabe: ''
        });
      } else {
        console.log('Editing existing client with UUID:', id);
        this.isNewClient = false;
        // Check if it's a UUID format (simple check)
        if (id && id.length > 10 && id.includes('-')) {
          this.loadClientByUuid(id);
        } else {
          console.error('Invalid client UUID:', id);
          this.snackBar.open(this.translate.instant('CLIENT_EDIT.ERROR_INVALID_UUID'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
          this.router.navigate(['/clients']);
          return;
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    // Handle tab query parameter
    this.route.queryParams.subscribe(params => {
      if (params['tab'] && this.tabGroup) {
        const tabName = params['tab'].toLowerCase();
        const tabIndex = this.getTabIndexByName(tabName);
        if (tabIndex !== -1) {
          setTimeout(() => {
            this.tabGroup.selectedIndex = tabIndex;
          }, 100);
        }
      } else if (params['activeTab']) {
        // Legacy support for numeric activeTab parameter
        setTimeout(() => {
          if (this.tabGroup) {
            this.tabGroup.selectedIndex = parseInt(params['activeTab']);
          }
        }, 100);
      }
    });
  }

  /**
   * Map tab name to tab index
   */
  private getTabIndexByName(tabName: string): number {
    const tabMap: { [key: string]: number } = {
      'information': 0,
      'kunde-information': 0,
      'kontakte': 1,
      'kontakt': 1,
      'standorte': 2,
      'standort': 2,
      'dokumente': 3,
      'dokument': 3
    };
    return tabMap[tabName] ?? -1;
  }

  createForm() {
    this.clientForm = this.formBuilder.group({
      suchbegriff: ['', [Validators.required]],
      status_id: [2],
      techniker_projekthinweis: [''],
      startdatum: [''],
      enddatum: [''],
      anmeldung_besuche: [''],
      periode_aufgaben_vorschlagen: [1],
      ueberpruefung_erforderlich: [''],
      bemerkung_next_evaluation: [''],
      wiederkehrende_aufgaben_vorhanden: [0],
      datum_letzte_wiederkehrende_aufgabe: [''],
      // New address fields
      strasse: ['', [Validators.maxLength(255)]],
      plz: ['', [Validators.maxLength(10)]],
      ort: ['', [Validators.maxLength(255)]],
      land: ['', [Validators.maxLength(100)]]
      // anmerkungen removed - now handled as JSON array
    });
  }

  loadClient(id: number) {
    console.log('Loading client with ID:', id);
    this.isLoading = true;
    this.subscription = this.kundeService.getKunde(id).subscribe(
      (response) => {
        console.log('Client data received:', response);
        this.client = response.data;
        console.log('Setting form values with:', response.data);
        
        // Parse anmerkungen JSON array
        this.anmerkungen = this.parseAnmerkungen(response.data.anmerkungen || '');
        
        // More robust form patching with field mapping
        const formData = {
          suchbegriff: response.data.suchbegriff || '',
          status_id: response.data.status_id || 1,
          techniker_projekthinweis: response.data.techniker_projekthinweis || '',
          startdatum: response.data.startdatum || '', // Keep ISO format for date inputs
          enddatum: response.data.enddatum || '', // Keep ISO format for date inputs
          anmeldung_besuche: response.data.anmeldung_besuche || '',
          periode_aufgaben_vorschlagen: response.data.periode_aufgaben_vorschlagen || 0,
          ueberpruefung_erforderlich: response.data.ueberpruefung_erforderlich || '',
          bemerkung_next_evaluation: response.data.bemerkung_next_evaluation || '',
          wiederkehrende_aufgaben_vorhanden: response.data.wiederkehrende_aufgaben_vorhanden || 0,
          datum_letzte_wiederkehrende_aufgabe: response.data.datum_letzte_wiederkehrende_aufgabe || '', // Keep ISO format for date inputs
          // New address fields
          strasse: response.data.strasse || '',
          plz: response.data.plz || '',
          ort: response.data.ort || '',
          land: response.data.land || ''
          // anmerkungen removed - handled separately as JSON array
        };
        
        console.log('Mapped form data:', formData);
        this.clientForm.patchValue(formData);
        console.log('Form values after patch:', this.clientForm.value);
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading client:', error);
        this.isLoading = false;
        this.snackBar.open(`${this.translate.instant('CLIENT_EDIT.ERROR_LOAD')}: ${error.status} ${error.statusText}`, this.translate.instant('COMMON.CLOSE'), {
          duration: 5000,
        });
      }
    );
  }

  loadClientByUuid(uuid: string) {
    console.log('Loading client with UUID:', uuid);
    this.isLoading = true;
    this.subscription = this.kundeService.getKundeByUuid(uuid).subscribe(
      (response) => {
        console.log('Client data received:', response);
        this.client = response.data;
        console.log('Setting form values with:', response.data);
        
        // Parse anmerkungen JSON array
        this.anmerkungen = this.parseAnmerkungen(response.data.anmerkungen || '');
        
        // Only populate form if we're in edit mode or creating new client
        if (this.isEditMode || this.isNewClient) {
          // More robust form patching with field mapping
          const formData = {
            suchbegriff: response.data.suchbegriff || '',
            status_id: response.data.status_id || 1,
            techniker_projekthinweis: response.data.techniker_projekthinweis || '',
            startdatum: response.data.startdatum || '', // Keep ISO format for date inputs
            enddatum: response.data.enddatum || '', // Keep ISO format for date inputs
            anmeldung_besuche: response.data.anmeldung_besuche || '',
            periode_aufgaben_vorschlagen: response.data.periode_aufgaben_vorschlagen || 0,
            ueberpruefung_erforderlich: response.data.ueberpruefung_erforderlich || '',
            bemerkung_next_evaluation: response.data.bemerkung_next_evaluation || '',
            wiederkehrende_aufgaben_vorhanden: response.data.wiederkehrende_aufgaben_vorhanden || 0,
            datum_letzte_wiederkehrende_aufgabe: response.data.datum_letzte_wiederkehrende_aufgabe || '', // Keep ISO format for date inputs
            // New address fields
            strasse: response.data.strasse || '',
            plz: response.data.plz || '',
            ort: response.data.ort || '',
            land: response.data.land || ''
            // anmerkungen removed - handled separately as JSON array
          };
          
          console.log('Mapped form data with ISO dates:', formData);
          this.clientForm.patchValue(formData);
          console.log('Form values after patch:', this.clientForm.value);
        }
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading client:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          url: error.url,
          body: error.body
        });
        this.isLoading = false;
        this.snackBar.open(`${this.translate.instant('CLIENT_EDIT.ERROR_LOAD')}: ${error.status} ${error.statusText}`, this.translate.instant('COMMON.CLOSE'), {
          duration: 5000,
        });
      }
    );
  }

  saveClient() {
    if (this.clientForm.valid) {
      this.isSaving = true;
      const formData = this.clientForm.value;
      
      // Convert German dates back to ISO format for API
      const clientData = {
        ...formData,
        startdatum: this.germanToIsoDate(formData.startdatum),
        enddatum: this.germanToIsoDate(formData.enddatum),
        datum_letzte_wiederkehrende_aufgabe: this.germanToIsoDate(formData.datum_letzte_wiederkehrende_aufgabe),
        // Serialize anmerkungen array to JSON string
        anmerkungen: this.serializeAnmerkungen(this.anmerkungen)
      };

      if (this.isNewClient) {
        this.kundeService.createKunde(clientData).subscribe(
          (response) => {
            this.isSaving = false;
            
            // Update parent component via service
            this.dataChangeClientService.updateData(response.data);
            
            // Log journal entry with entity IDs
            const entityIds = this.journalService.extractEntityIds(response.data);
            this.journalService.log(
              'Kunde erstellt',
              '', // aktenzeichen (legacy, not needed with entity IDs)
              `Kunde: ${response.data?.suchbegriff || 'Unbekannt'}`,
              entityIds
            );
            
            this.snackBar.open(
              this.translate.instant('CLIENT_EDIT.SUCCESS_CREATED'),
              this.translate.instant('COMMON.CLOSE'),
              { duration: 3000 }
            );
            
            // Navigate to client detail page (view mode) instead of list
            const clientUuid = response.data.uuid;
            if (clientUuid) {
              this.router.navigate(['/client-edit', clientUuid]);
            } else {
              // Fallback if UUID is not available
              console.error('No UUID in response after creating client');
              this.router.navigate(['/clients']);
            }
          },
          (error) => {
            this.isSaving = false;
            this.snackBar.open(
              this.translate.instant('CLIENT_EDIT.ERROR_CREATE'),
              this.translate.instant('COMMON.CLOSE'),
              { duration: 3000 }
            );
          }
        );
      } else {
        // For existing client, ensure we have a valid UUID
        const clientId = this.client?.uuid;
        if (!clientId) {
          this.snackBar.open(this.translate.instant('CLIENT_EDIT.ERROR_NO_ID'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
          this.isSaving = false;
          return;
        }

        this.kundeService.updateKunde(clientId, clientData).subscribe(
          (response) => {
            this.isSaving = false;
            
            // Update the local client data with the response
            // Merge response with existing client to preserve all fields (id, uuid, etc.)
            if (response.data) {
              this.client = {
                ...this.client,  // Preserve existing fields
                ...response.data,  // Update with response data
                anmerkungen: response.data.anmerkungen  // Ensure anmerkungen is updated
              };
              // Re-parse anmerkungen from response to ensure consistency
              this.anmerkungen = this.parseAnmerkungen(response.data.anmerkungen || '');
            }
            
            // Update parent component via service
            this.dataChangeClientService.updateData(response.data);
            
            // Log journal entry with entity IDs
            const entityIds = this.journalService.extractEntityIds(response.data || this.client);
            this.journalService.log(
              'Kunde aktualisiert',
              '', // aktenzeichen (legacy, not needed with entity IDs)
              `Kunde: ${response.data?.suchbegriff || this.client?.suchbegriff || 'Unbekannt'}`,
              entityIds
            );
            
            // Switch back to view mode instead of redirecting
            this.isEditMode = false;
            
            this.snackBar.open(
              this.translate.instant('CLIENT_EDIT.SUCCESS_UPDATED'),
              this.translate.instant('COMMON.CLOSE'),
              { duration: 3000 }
            );
          },
          (error) => {
            this.isSaving = false;
            this.snackBar.open(
              this.translate.instant('CLIENT_EDIT.ERROR_UPDATE'),
              this.translate.instant('COMMON.CLOSE'),
              { duration: 3000 }
            );
          }
        );
      }
    }
  }

  cancel() {
    // Switch back to view mode instead of redirecting
    this.isEditMode = false;
  }

  goBackToList() {
    this.router.navigate(['/clients']);
  }

  deleteClient() {
    if (!this.client) {
      this.snackBar.open(this.translate.instant('CLIENT_EDIT.ERROR_NO_CLIENT'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Kunde löschen',
        message:
          `Möchten Sie den Kunden "${this.client.suchbegriff}" wirklich löschen?\n\n` +
          `Folgende Aktionen werden durchgeführt:\n` +
          `- Der Kunde wird als inaktiv markiert\n` +
          `- Alle Standorte werden als inaktiv markiert\n` +
          `- Alle aktiven Kontrolltermine werden als gelöscht markiert\n` +
          `- Die Projektaufgabe wird als inaktiv markiert\n` +
          `- Die Konten aller Kontakte werden entfernt (Kontakte können sich nicht mehr anmelden)\n\n` +
          `Hinweis: Alle Daten werden als inaktiv markiert und nicht dauerhaft gelöscht.\n` +
          `Die Daten bleiben für Audit-Zwecke erhalten und können bei Bedarf wiederhergestellt werden.`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Use UUID for deletion if available, otherwise fall back to ID
        const identifier = this.client?.uuid || this.client?.id;
        
        if (!identifier) {
          this.snackBar.open(this.translate.instant('CLIENT_EDIT.ERROR_NO_ID'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
          return;
        }
        
        this.kundeService.deleteKunde(identifier).subscribe(
          (response) => {
            // Log journal entry with entity IDs
            const entityIds = this.journalService.extractEntityIds(this.client);
            this.journalService.log(
              'Kunde gelöscht (deaktiviert)',
              '', // aktenzeichen (legacy, not needed with entity IDs)
              `Kunde: ${this.client?.suchbegriff || 'Unbekannt'}`,
              entityIds
            );
            
            this.snackBar.open(this.translate.instant('CLIENT_EDIT.SUCCESS_DEACTIVATED'), this.translate.instant('COMMON.CLOSE'), {
              duration: 3000,
            });
            // Navigate back to client list
            this.router.navigate(['/clients']);
          },
          (error) => {
            console.error('Error deleting client:', error);
            this.snackBar.open(this.translate.instant('CLIENT_EDIT.ERROR_DELETE'), this.translate.instant('COMMON.CLOSE'), {
              duration: 5000,
            });
          }
        );
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.clientForm.get(fieldName);
    return control ? control.invalid && control.touched : false;
  }

  // Debug method to check form state
  debugFormState() {
    console.log('=== FORM DEBUG INFO ===');
    console.log('Form valid:', this.clientForm.valid);
    console.log('Form dirty:', this.clientForm.dirty);
    console.log('Form touched:', this.clientForm.touched);
    console.log('Form values:', this.clientForm.value);
    console.log('Form errors:', this.clientForm.errors);
    
    // Check each field
    Object.keys(this.clientForm.controls).forEach(key => {
      const control = this.clientForm.get(key);
      console.log(`Field ${key}:`, {
        value: control?.value,
        valid: control?.valid,
        dirty: control?.dirty,
        touched: control?.touched,
        errors: control?.errors
      });
    });
  }



  getStatusText(statusId: number): string {
    // Use environment client_status array
    const statusArray = (environment as any).client_status || [];
    const status = statusArray.find((s: any) => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }

  enterEditMode() {
    this.isEditMode = true;
    
    // Patch the form with current client data
    if (this.client) {
      this.clientForm.patchValue({
        suchbegriff: this.client.suchbegriff || '',
        status_id: this.client.status_id || 1,
        techniker_projekthinweis: this.client.techniker_projekthinweis || '',
        startdatum: this.client.startdatum || '', // Keep ISO format for date inputs
        enddatum: this.client.enddatum || '', // Keep ISO format for date inputs
        anmeldung_besuche: this.client.anmeldung_besuche || '',
        periode_aufgaben_vorschlagen: this.client.periode_aufgaben_vorschlagen || 0,
        ueberpruefung_erforderlich: this.client.ueberpruefung_erforderlich || '',
        bemerkung_next_evaluation: this.client.bemerkung_next_evaluation || '',
        wiederkehrende_aufgaben_vorhanden: this.client.wiederkehrende_aufgaben_vorhanden || 0,
        datum_letzte_wiederkehrende_aufgabe: this.client.datum_letzte_wiederkehrende_aufgabe || '', // Keep ISO format for date inputs
        // Address fields
        strasse: this.client.strasse || '',
        plz: this.client.plz || '',
        ort: this.client.ort || '',
        land: this.client.land || ''
        // anmerkungen removed - handled separately as JSON array
      });
      
      // Parse anmerkungen JSON array
      this.anmerkungen = this.parseAnmerkungen(this.client.anmerkungen || '');
      
      console.log('Form patched in edit mode with ISO dates:', {
        startdatum: this.client.startdatum,
        enddatum: this.client.enddatum,
        datum_letzte_wiederkehrende_aufgabe: this.client.datum_letzte_wiederkehrende_aufgabe
      });
    }
  }

  cancelEdit() {
    this.isEditMode = false;
  }

  /**
   * Get the current active tab index
   */
  getCurrentTabIndex(): number {
    return this.tabGroup?.selectedIndex || 0;
  }

  /**
   * Convert ISO date (YYYY-MM-DD) to German format (DD.MM.YYYY)
   */
  private isoToGermanDate(isoDate: string): string {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return isoDate; // Return original if invalid
    return this.datePipe.transform(date, 'dd.MM.yyyy') || isoDate;
  }

  /**
   * Convert German date (DD.MM.YYYY) to ISO format (YYYY-MM-DD)
   */
  private germanToIsoDate(germanDate: string): string {
    if (!germanDate) return '';
    // Check if it's already in ISO format
    if (germanDate.match(/^\d{4}-\d{2}-\d{2}$/)) return germanDate;
    
    // Convert from DD.MM.YYYY to YYYY-MM-DD
    const parts = germanDate.split('.');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return germanDate;
  }

  /**
   * Format date for display in German format
   */
  formatDateForDisplay(dateString: string): string {
    return this.isoToGermanDate(dateString);
  }

  /**
   * Parse anmerkungen from JSON string with backward compatibility
   */
  parseAnmerkungen(jsonString: string): Anmerkung[] {
    if (!jsonString || jsonString.trim() === '') {
      return [];
    }

    try {
      // Try to parse as JSON array
      if (jsonString.trim().startsWith('[')) {
        const parsed = JSON.parse(jsonString);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
      
      // If it's plain text (old format), convert to single Anmerkung entry
      // Use current user info if available, otherwise use placeholder
      const currentUserId = String(this.authQuery.userid || 'unknown');
      const currentUsername = this.authQuery.name || this.authQuery.email || 'Unbekannt';
      
      return [{
        user_id: currentUserId,
        username: currentUsername,
        anmerkung: jsonString,
        datetime: new Date().toISOString()
      }];
    } catch (e) {
      console.error('Error parsing anmerkungen:', e);
      // If parsing fails, treat as plain text (backward compatibility)
      const currentUserId = String(this.authQuery.userid || 'unknown');
      const currentUsername = this.authQuery.name || this.authQuery.email || 'Unbekannt';
      
      return [{
        user_id: currentUserId,
        username: currentUsername,
        anmerkung: jsonString,
        datetime: new Date().toISOString()
      }];
    }
  }

  /**
   * Serialize anmerkungen array to JSON string
   */
  serializeAnmerkungen(anmerkungen: Anmerkung[]): string {
    if (!anmerkungen || anmerkungen.length === 0) {
      return '';
    }
    return JSON.stringify(anmerkungen);
  }

  /**
   * Check if current user can edit/delete anmerkungen (admin only)
   */
  canEditAnmerkungen(): boolean {
    const role = this.authQuery.role;
    // Role 1 = SysAdmin, Role 2 = Admin
    return role === 1 || role === 2;
  }

  /**
   * Add new anmerkung entry (called from template without parameter)
   */
  addAnmerkungFromTemplate(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log('addAnmerkungFromTemplate called, newAnmerkungText:', this.newAnmerkungText);
    
    if (!this.newAnmerkungText || this.newAnmerkungText.trim() === '') {
      console.log('Text is empty, returning');
      return;
    }
    
    this.addAnmerkung(this.newAnmerkungText);
  }

  /**
   * Add new anmerkung entry
   */
  addAnmerkung(text: string): void {
    if (!text || text.trim() === '') {
      return;
    }

    const currentUserId = this.authQuery.userid;
    const currentUsername = this.authQuery.name || this.authQuery.email || 'Unbekannt';

    if (!currentUserId) {
      this.snackBar.open(this.translate.instant('CLIENT_EDIT.ERROR_USER_ID'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
      return;
    }

    this.isAddingAnmerkung = true;

    const newAnmerkung: Anmerkung = {
      user_id: String(currentUserId),
      username: currentUsername,
      anmerkung: text.trim(),
      datetime: new Date().toISOString()
    };

    // Append to array (chat style - no deletion)
    this.anmerkungen = [...this.anmerkungen, newAnmerkung];

    // Save immediately to client (only if client exists)
    if (this.client && this.client.uuid && !this.isNewClient) {
      const clientData = {
        ...this.clientForm.value,
        anmerkungen: this.serializeAnmerkungen(this.anmerkungen)
      };

      this.kundeService.updateKunde(this.client.uuid, clientData).subscribe({
        next: (response) => {
          this.isAddingAnmerkung = false;
          this.newAnmerkungText = '';
          // Update client data with response to keep anmerkungen in sync
          // Merge response with existing client to preserve all fields (id, uuid, etc.)
          if (response.data) {
            this.client = {
              ...this.client,  // Preserve existing fields
              ...response.data,  // Update with response data
              anmerkungen: response.data.anmerkungen  // Ensure anmerkungen is updated
            };
            // Re-parse anmerkungen from response to ensure consistency
            this.anmerkungen = this.parseAnmerkungen(response.data.anmerkungen || '');
          }
          this.snackBar.open(this.translate.instant('CLIENT_EDIT.NOTE_ADDED'), this.translate.instant('COMMON.CLOSE'), { duration: 2000 });
        },
        error: (error) => {
          this.isAddingAnmerkung = false;
          // Revert the change
          this.anmerkungen = this.anmerkungen.slice(0, -1);
          this.snackBar.open(this.translate.instant('CLIENT_EDIT.ERROR_NOTE_ADD'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        }
      });
    } else {
      // For new clients, just add to array (will be saved when client is created)
      this.isAddingAnmerkung = false;
      this.newAnmerkungText = '';
      this.snackBar.open(this.translate.instant('CLIENT_EDIT.NOTE_ADDED_PENDING'), this.translate.instant('COMMON.CLOSE'), { duration: 2000 });
    }
  }

  /**
   * Find anmerkung index in original array by matching datetime and user_id
   * Public method for template use
   */
  findAnmerkungIndex(anmerkung: Anmerkung): number {
    return this.anmerkungen.findIndex(a => 
      a.datetime === anmerkung.datetime && 
      a.user_id === anmerkung.user_id &&
      a.anmerkung === anmerkung.anmerkung
    );
  }

  /**
   * Start editing anmerkung (admin only)
   */
  startEditAnmerkung(anmerkung: Anmerkung): void {
    if (!this.canEditAnmerkungen()) {
      return;
    }
    const index = this.findAnmerkungIndex(anmerkung);
    if (index !== -1) {
      // Store original for error recovery
      this.originalAnmerkungenBeforeEdit = [...this.anmerkungen];
      this.editingAnmerkungIndex = index;
      this.editingAnmerkungText = this.anmerkungen[index].anmerkung;
    }
  }

  /**
   * Cancel editing anmerkung
   */
  cancelEditAnmerkung(): void {
    this.editingAnmerkungIndex = null;
    this.editingAnmerkungText = '';
    this.originalAnmerkungenBeforeEdit = [];
  }

  /**
   * Save edited anmerkung (admin only)
   */
  saveEditAnmerkung(): void {
    if (!this.canEditAnmerkungen() || this.editingAnmerkungIndex === null) {
      return;
    }

    if (!this.editingAnmerkungText || this.editingAnmerkungText.trim() === '') {
      this.snackBar.open(this.translate.instant('CLIENT_EDIT.NOTE_EMPTY'), this.translate.instant('COMMON.CLOSE'), { duration: 2000 });
      return;
    }

    const index = this.editingAnmerkungIndex;
    const updatedAnmerkungen = [...this.anmerkungen];
    updatedAnmerkungen[index] = {
      ...updatedAnmerkungen[index],
      anmerkung: this.editingAnmerkungText.trim()
    };

    this.anmerkungen = updatedAnmerkungen;

    // Save to client
    if (this.client && this.client.uuid) {
      const clientData = {
        ...this.clientForm.value,
        anmerkungen: this.serializeAnmerkungen(this.anmerkungen)
      };

      this.kundeService.updateKunde(this.client.uuid, clientData).subscribe({
        next: (response) => {
          this.editingAnmerkungIndex = null;
          this.editingAnmerkungText = '';
          this.originalAnmerkungenBeforeEdit = [];
          // Update client data with response to keep anmerkungen in sync
          // Merge response with existing client to preserve all fields (id, uuid, etc.)
          if (response.data) {
            this.client = {
              ...this.client,  // Preserve existing fields
              ...response.data,  // Update with response data
              anmerkungen: response.data.anmerkungen  // Ensure anmerkungen is updated
            };
            // Re-parse anmerkungen from response to ensure consistency
            this.anmerkungen = this.parseAnmerkungen(response.data.anmerkungen || '');
          }
          this.snackBar.open(this.translate.instant('CLIENT_EDIT.NOTE_UPDATED'), this.translate.instant('COMMON.CLOSE'), { duration: 2000 });
        },
        error: (error) => {
          // Revert the change
          this.anmerkungen = [...this.originalAnmerkungenBeforeEdit];
          this.originalAnmerkungenBeforeEdit = [];
          this.editingAnmerkungIndex = null;
          this.editingAnmerkungText = '';
          this.snackBar.open(this.translate.instant('CLIENT_EDIT.ERROR_NOTE_UPDATE'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
        }
      });
    } else {
      this.editingAnmerkungIndex = null;
      this.editingAnmerkungText = '';
    }
  }

  /**
   * Delete anmerkung (admin only)
   */
  deleteAnmerkung(anmerkung: Anmerkung): void {
    if (!this.canEditAnmerkungen()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Anmerkung löschen',
        message: 'Möchten Sie diese Anmerkung wirklich löschen?'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const index = this.findAnmerkungIndex(anmerkung);
        if (index === -1) {
          this.snackBar.open(this.translate.instant('CLIENT_EDIT.NOTE_NOT_FOUND'), this.translate.instant('COMMON.CLOSE'), { duration: 2000 });
          return;
        }
        // Store original for error recovery
        const originalAnmerkungen = [...this.anmerkungen];
        const updatedAnmerkungen = this.anmerkungen.filter((_, i) => i !== index);
        this.anmerkungen = updatedAnmerkungen;

        // Save to client
        if (this.client && this.client.uuid) {
          const clientData = {
            ...this.clientForm.value,
            anmerkungen: this.serializeAnmerkungen(this.anmerkungen)
          };

          this.kundeService.updateKunde(this.client.uuid, clientData).subscribe({
            next: (response) => {
              // Update client data with response to keep anmerkungen in sync
              // Merge response with existing client to preserve all fields (id, uuid, etc.)
              if (response.data) {
                this.client = {
                  ...this.client,  // Preserve existing fields
                  ...response.data,  // Update with response data
                  anmerkungen: response.data.anmerkungen  // Ensure anmerkungen is updated
                };
                // Re-parse anmerkungen from response to ensure consistency
                this.anmerkungen = this.parseAnmerkungen(response.data.anmerkungen || '');
              }
              this.snackBar.open(this.translate.instant('CLIENT_EDIT.NOTE_DELETED'), this.translate.instant('COMMON.CLOSE'), { duration: 2000 });
            },
            error: (error) => {
              // Revert the change
              this.anmerkungen = originalAnmerkungen;
              this.snackBar.open(this.translate.instant('CLIENT_EDIT.ERROR_NOTE_DELETE'), this.translate.instant('COMMON.CLOSE'), { duration: 3000 });
            }
          });
        }
      }
    });
  }

  /**
   * Format anmerkung date for display
   */
  formatAnmerkungDate(datetime: string): string {
    if (!datetime) return '';
    try {
      const date = new Date(datetime);
      if (isNaN(date.getTime())) return datetime;
      return this.datePipe.transform(date, 'dd.MM.yyyy HH:mm') || datetime;
    } catch (e) {
      return datetime;
    }
  }

  /**
   * Get user initials for avatar
   */
  getUserInitials(username: string): string {
    if (!username) return '?';
    const parts = username.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return username.substring(0, 2).toUpperCase();
  }
}
